/**
 * Which chooser "Change it" should send the operator back to (DNKADM-0396).
 *
 * DNKADM-0386 U1 had one answer — the coach — because `STAFF_CONFLICT` was the only
 * refusal. DNKAPI-0549 added a bay refusal, so returning to the Staff chooser after "Bay 2
 * is already booked" would point at the wrong field. The answer is **read from the codes
 * the server named**, never guessed from form state:
 *
 *  - any coach-side reason (`STAFF_CONFLICT`, `STAFF_OUTSIDE_HOURS`, `STAFF_OVER_CAP`) →
 *    the Staff chooser. A mixed refusal lands here deliberately: the coach reasons are the
 *    ones with a person attached, and the bay is one field away.
 *  - only `RESOURCE_CONFLICT` → the Bay chooser.
 *  - a code the console does not know → the Staff chooser, the pre-0396 behavior. It is a
 *    focus target, not a claim: a host with no Bay chooser simply no-ops.
 */
import type { ConflictRefusal } from '@/api/reservations';

/** The coach-side refusal codes — the two DNKAPI-0549 schedule reasons plus DNKAPI-0542's. */
const STAFF_SIDE_CODES: readonly string[] = [
  'STAFF_CONFLICT',
  'STAFF_OUTSIDE_HOURS',
  'STAFF_OVER_CAP',
];

export function refusedPicker(refusal: ConflictRefusal): 'staff' | 'resource' {
  const everyReasonIsTheBay =
    refusal.reasons.length > 0 &&
    refusal.reasons.every((reason) => reason.code === 'RESOURCE_CONFLICT');
  return everyReasonIsTheBay ? 'resource' : 'staff';
}

/** True when this refusal names at least one coach-side reason — the annotation hosts use. */
export function hasStaffSideReason(refusal: ConflictRefusal): boolean {
  return refusal.reasons.some(
    (reason) => reason.code !== null && STAFF_SIDE_CODES.includes(reason.code),
  );
}
